import { Button, Text } from '@mantine/core'
import { EOB_RESPONSIBILITY_KINDS, type PatientInvoiceKind } from '@/model/middleware-types'
import { PATIENT_INVOICE_KIND_LABELS } from '@/model/patient-invoice-kind'
import {
  PILL_STYLE_AMBER,
  PILL_STYLE_GRAY,
  PILL_STYLE_GREEN,
  PILL_STYLE_RED,
  formatCents,
  type StatusPillStyle,
} from '../utils'
import type { BillingCaseDetail } from './CaseDrawer'
import { StatusPill } from './StatusPill'

type PatientInvoice = BillingCaseDetail['patientInvoices'][number]

/** One action per row: Add (no Invoice yet), Collect (draft or link sent), Refund (paid). */
export type PatientPaymentAction = 'add' | 'collect' | 'refund'

type PatientResponsibilitySectionProps = {
  invoices: PatientInvoice[]
  patientRespCents: number | null
  /** Σ processed patient payments of every kind. */
  patientPaidCents: number | null
  /** patientResp − patientPaid; null until the EOB is entered. */
  patientBalanceCents: number | null
  selfPay: boolean
  onAction: (kind: PatientInvoiceKind, action: PatientPaymentAction) => void
}

function statusPill(invoice: PatientInvoice): { label: string; style: StatusPillStyle } {
  switch (invoice.status) {
    case 'draft':
      return { label: 'Draft', style: PILL_STYLE_GRAY }
    case 'issued':
      return invoice.stripeStatus === 'expired'
        ? { label: 'Link expired', style: PILL_STYLE_RED }
        : { label: 'Link sent', style: PILL_STYLE_AMBER }
    case 'balanced':
      return { label: invoice.collectedOnDos ? 'Paid on DOS' : 'Paid', style: PILL_STYLE_GREEN }
    case 'cancelled':
      return { label: 'Refunded', style: PILL_STYLE_GRAY }
  }
}

function actionFor(invoice: PatientInvoice | undefined): PatientPaymentAction | null {
  if (!invoice) return 'add'
  if (invoice.status === 'draft' || invoice.status === 'issued') return 'collect'
  if (invoice.status === 'balanced') return 'refund'
  return null
}

function Figure({ label, cents }: { label: string; cents: number | null }) {
  return (
    <div className="flex flex-col">
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={600} c={cents === null ? 'dimmed' : 'dark'} className="tabular-nums">
        {cents === null ? '—' : formatCents(cents)}
      </Text>
    </div>
  )
}

/** The one number the biller is after: what is still to be collected from the patient. */
function LeftToCollect({ cents }: { cents: number | null }) {
  if (cents === null) {
    return (
      <div className="flex flex-col items-end gap-1">
        <StatusPill style={PILL_STYLE_GRAY}>Left to collect —</StatusPill>
        <Text size="xs" c="dimmed">
          Enter the EOB to see what the patient owes
        </Text>
      </div>
    )
  }
  if (cents === 0) return <StatusPill style={PILL_STYLE_GREEN}>Left to collect $0</StatusPill>
  if (cents > 0) {
    return <StatusPill style={PILL_STYLE_AMBER}>Left to collect {formatCents(cents)}</StatusPill>
  }
  return <StatusPill style={PILL_STYLE_RED}>Credit {formatCents(-cents)} — review</StatusPill>
}

const ACTION_LABEL: Record<PatientPaymentAction, string> = {
  add: 'Add',
  collect: 'Collect',
  refund: 'Refund',
}

export function PatientResponsibilitySection({
  invoices,
  patientRespCents,
  patientPaidCents,
  patientBalanceCents,
  selfPay,
  onAction,
}: PatientResponsibilitySectionProps) {
  const byKind = new Map(invoices.map((invoice) => [invoice.kind, invoice]))
  const lockedCopay = byKind.get('copay')
  const overpaid =
    lockedCopay &&
    lockedCopay.status !== 'draft' &&
    patientRespCents !== null &&
    lockedCopay.amountCents > patientRespCents

  return (
    <div className="flex flex-col gap-2">
      {/* A self-pay visit has no EOB: the fee is the only patient money, so only what was paid
          is summed — there is no responsibility to collect against. */}
      <div className="flex items-center justify-between gap-3 rounded-md bg-slate-50 px-3 py-2">
        <div className="flex items-baseline gap-5">
          {!selfPay && <Figure label="Responsibility" cents={patientRespCents} />}
          <Figure label="Patient paid" cents={patientPaidCents} />
        </div>
        {!selfPay && <LeftToCollect cents={patientBalanceCents} />}
      </div>

      {(selfPay ? (['self-pay'] as const) : EOB_RESPONSIBILITY_KINDS).map((kind) => {
        const invoice = byKind.get(kind)
        const action = actionFor(invoice)

        // Kind · status · amount · one action. Kept flat (no sub-wrappers) so the row is
        // the label's parent for anyone reading it as a unit.
        return (
          <div
            key={kind}
            className="flex items-center gap-3 rounded-md border border-slate-200 px-3 py-2"
          >
            <Text size="sm" fw={600} c="dark" className="w-24 shrink-0">
              {PATIENT_INVOICE_KIND_LABELS[kind]}
            </Text>
            {invoice ? (
              <StatusPill style={statusPill(invoice).style}>{statusPill(invoice).label}</StatusPill>
            ) : (
              <StatusPill style={PILL_STYLE_GRAY}>Not set</StatusPill>
            )}
            {invoice?.manual ? <StatusPill style={PILL_STYLE_GRAY}>Manual</StatusPill> : null}
            <Text
              size="sm"
              fw={600}
              c={invoice ? 'dark' : 'dimmed'}
              className="ml-auto tabular-nums"
            >
              {invoice ? formatCents(invoice.amountCents) : '—'}
            </Text>
            {action ? (
              <Button
                variant={action === 'refund' ? 'subtle' : 'light'}
                size="compact-sm"
                w={88}
                onClick={() => onAction(kind, action)}
              >
                {ACTION_LABEL[action]}
              </Button>
            ) : (
              <span className="w-[88px] shrink-0" />
            )}
          </div>
        )
      })}
      {overpaid ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
          <Text size="sm" c="dark">
            Patient paid more than their responsibility
          </Text>
        </div>
      ) : null}
    </div>
  )
}
